// Constants for Grand Pittu Admin Panel

/**
 * Configuration constants and settings for the Grand Pittu Admin application
 */

// Environment Configuration
const isDevelopment = import.meta.env.VITE_API_URL;
const isProduction = import.meta.env.PROD;

// API Configuration
export const API_CONFIG = {
  BASE_URL: import.meta.env.VITE_API_URL,
  VERSION: 'v1',
  SLUG: '/api/v1',
  // Extended timeouts - booking creation with table merging can take longer
  // Increased to 60s to prevent AbortError causing booking failures
  TIMEOUT: 60000, // 60 seconds for all environments
  RETRY_ATTEMPTS: 3,
  RETRY_DELAY: 2000, // 2 seconds between retries
  EXPONENTIAL_BACKOFF: true, // Enable exponential backoff for retries
} as const;


// WebSocket Configuration
export const WEBSOCKET_CONFIG = {
  // WebSocket URL - SockJS uses HTTP/HTTPS and upgrades to WebSocket
  // Full endpoint: https://admin.grandpittu.lk/api/api/v1/ws
  WS_URL: import.meta.env.VITE_WS_URL || 'https://admin.grandpittu.lk',
  SLUG_WS: '/api/v1/ws',
  // Connection settings
  RECONNECT_DELAY: 2000, // 2 seconds
  MAX_RECONNECT_ATTEMPTS: isDevelopment ? 5 : 3, // More attempts in development
  HEARTBEAT_INCOMING: 4000,
  HEARTBEAT_OUTGOING: 4000,

  // Enable debug logging in development
  DEBUG: isDevelopment
} as const;

// App Configuration
export const APP_CONFIG = {
  NAME: import.meta.env.VITE_APP_NAME || 'Grand Pittu Admin',
  VERSION: import.meta.env.VITE_APP_VERSION || '1.0.0',
  DESCRIPTION: 'Administration panel for Grand Pittu Restaurant',
  COMPANY: 'Finspire Software Solutions',
  CONTACT_EMAIL: 'support@finspire.tech',
  WEBSITE: 'https://grandpittu.lk',
  ENVIRONMENT: import.meta.env.NODE_ENV || 'development',

} as const;

// Security Configuration
export const SECURITY_CONFIG = {
  ENABLE_DEV_TOOLS: import.meta.env.VITE_ENABLE_DEV_TOOLS === 'true' || isDevelopment,
  TOKEN_REFRESH_THRESHOLD: 5 * 60 * 1000, // Refresh token 5 minutes before expiry
  SESSION_TIMEOUT: 24 * 60 * 60 * 1000, // 24 hours
  CSRF_PROTECTION: isProduction,
  SECURE_COOKIES: isProduction,
} as const;

// Feature Flags
export const FEATURES = {
  DARK_MODE: true,
  ANALYTICS: import.meta.env.VITE_ENABLE_ANALYTICS === 'true' && isProduction,
  NOTIFICATIONS: import.meta.env.VITE_ENABLE_NOTIFICATIONS !== 'false',
  EXPORT: true,
  BULK_OPERATIONS: true,
  REAL_TIME_UPDATES: false,
  DEV_TOOLS: isDevelopment,
} as const;

// UI Configuration
export const UI_CONFIG = {
  SIDEBAR_WIDTH: 280,
  SIDEBAR_COLLAPSED_WIDTH: 64,
  HEADER_HEIGHT: 64,
  TOAST_DURATION: 4000,
  ANIMATION_DURATION: 200,
  DEBOUNCE_DELAY: 300,
} as const;

// Pagination Configuration
export const PAGINATION_CONFIG = {
  DEFAULT_PAGE_SIZE: 10,
  PAGE_SIZE_OPTIONS: [5, 10, 20, 50, 100],
  MAX_VISIBLE_PAGES: 7,
} as const;

// Cache Configuration
export const CACHE_CONFIG = {
  DEFAULT_TTL: 5 * 60 * 1000, // 5 minutes
  MAX_ENTRIES: 100,
  STORAGE_KEY_PREFIX: 'gp_admin_cache_',
} as const;

// File Upload Configuration
export const UPLOAD_CONFIG = {
  MAX_FILE_SIZE: 10 * 1024 * 1024, // 10MB
  ALLOWED_IMAGE_TYPES: ['image/jpeg', 'image/png', 'image/webp', 'image/svg+xml'],
  ALLOWED_DOCUMENT_TYPES: ['application/pdf', 'text/plain', 'application/msword'],
  MAX_FILES: 5,
} as const;

// Validation Rules
export const VALIDATION = {
  PASSWORD_MIN_LENGTH: 8,
  PASSWORD_REQUIRE_NUMBERS: true,
  PASSWORD_REQUIRE_SPECIAL: true,
  EMAIL_PATTERN: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  PHONE_PATTERN: /^\+?[\d\s\-()]{10,}$/,
  NAME_MIN_LENGTH: 2,
  NAME_MAX_LENGTH: 50,
} as const;

// Date/Time Configuration
export const DATE_CONFIG = {
  DEFAULT_FORMAT: 'YYYY-MM-DD',
  DATETIME_FORMAT: 'YYYY-MM-DD HH:mm:ss',
  DISPLAY_FORMAT: 'MMM DD, YYYY',
  TIME_FORMAT: 'HH:mm',
  TIMEZONE: 'UTC',
} as const;

// OAuth Configuration
export const OAUTH_CONFIG = {
  ZOHO: {
    CLIENT_ID: import.meta.env.VITE_ZOHO_CLIENT_ID || '',
    REDIRECT_URI: import.meta.env.VITE_ZOHO_REDIRECT_URI ||
      `${globalThis.window?.location.origin ?? ''}/auth/callback`,
    ENABLED: !!import.meta.env.VITE_ZOHO_CLIENT_ID,
  },
} as const;

// Development utilities
export const DEV_CONFIG = {
  LOG_LEVEL: isDevelopment ? 'debug' : 'error',
  ENABLE_LOGGER: isDevelopment,
  MOCK_API: false,
  SHOW_PERFORMANCE: isDevelopment,
} as const;



// Backend API endpoints - Complete list based on backend documentation
export const API_ENDPOINTS = {
  // Admin booking management
  ADMIN: {
    MANAGE_BOOKING: '/admin/manage-booking',
    GET_ACCEPTED_BOOKINGS: '/admin/getAllBookingStatusAcceptedByFloorId',  // GET ?floorId=1&type=all
    GET_PENDING_BOOKINGS: '/admin/getAllBookingStatusPending',
    GET_OTHER_STATUS_BOOKINGS: '/admin/getAllBookingOtherStatus',
  },
  // Restaurant table management
  RESTAURANT_TABLE: {
    BASE: '/restaurantTable',
    GET_ALL: '/restaurantTable/getAll',
    BY_ID: (id: number) => `/restaurantTable/${id}`,
    BY_FLOORID: (floorId: number) => `/restaurantTable/getAll/${floorId}`,
    UPDATE_STATUS: '/restaurantTable/updateStatus',
    GET_AVAILABLE_BY_DATE_TIME: '/restaurantTable/getAvailableTableByDateAndTime',  // GET ?date=YYYY-MM-DD&time=HH:MM&floorId=1
  },
  // Floor management
  FLOOR: {
    BASE: '/floor',
    GET_ALL: '/floor/getAll',
    BY_ID: (id: number) => `/floor/${id}`,
    GET_DETAILS_BY_ID: (id: number) => `/floor/getFloorDetailsWithTablesAndAreasById/${id}`,
    GET_ALL_DETAILS: '/floor/getAllFloorDetailsWithTablesAndAreas',
  },
  // Reserve table operations
  RESERVE_TABLE: {
    CHECK_AVAILABILITY: '/reserveTable/checkAvailability',
    SEARCH: '/reserveTable/search',
    SEARCH_BY_PERIOD: '/reserveTable/searchByPeriod',
    GET_ALL_MERGE_GROUP: '/reserveTable/getAllMergeGroup',  // GET ?date=YYYY-MM-DD&floorId=2
    DELETE_MERGE_GROUP: (id: number) => `/reserveTable/deleteMergeGroup/${id}`,  // DELETE ?status=CANCELED
  },
  // Area management
  AREA: {
    BASE: '/area',
    GET_ALL: '/area/getAll',
    BY_ID: (id: number) => `/area/${id}`,
  },
  // Divider management
  DIVIDER: {
    BASE: '/divider',
    GET_ALL: '/divider/getAll',
    BY_ID: (id: number) => `/divider/${id}`,
  },
  // Notification management
  NOTIFICATION: {
    BY_ID: (id: number) => `/notification/${id}`,
    GET_ALL_BY_PERIOD: '/notification/getAllByPeriod',
  },
  // Public booking endpoint
  BOOKING: {
    CREATE: '/book',
  },
  // Dashboard/Analytics endpoints
  DASHBOARD: {
    STATS: '/dashboard/stats',
    STATS_BY_MONTH: '/dashboard/stats/month',
    STATS_CURRENT_MONTH: '/dashboard/stats/current-month',
    TRENDS_MONTHLY: '/dashboard/trends/monthly',
    TRENDS_DAILY: '/dashboard/trends/daily',
    TRENDS_DAILY_BY_MONTH: '/dashboard/trends/daily/month',
  },
} as const;

export const BRAND_COLORS = {
  primary: '#d4af37',
  secondary: '#8b0000',
  accent: '#ff6b35',
  success: '#10b981',
  warning: '#f59e0b',
  error: '#ef4444',
  info: '#3b82f6',
} as const;

export const USER_ROLES = {
  SUPER_ADMIN: 'super-admin',
  MANAGER: 'manager',
  EDITOR: 'editor',
  VIEWER: 'viewer',
} as const;

export const RESERVATION_STATUSES = {
  PENDING: 'pending',
  ACCEPTED: 'accepted',
  REJECTED: 'rejected',
  CANCELLED: 'cancelled',
  COMPLETED: 'completed',
  INCOMPLETE: 'incomplete',
} as const;

export const CONTENT_TYPES = {
  GLOBAL: 'global',
  PAGE_CONTENTS: 'page-contents',
  MENU_ITEMS: 'menu-items',
  CATEGORIES: 'categories',
  GALLERIES: 'galleries',
  SLIDERS: 'sliders',
  TESTIMONIALS: 'testimonials',
  SERVICES: 'services',
} as const;
